/**
 * FinancialProfileSection — Financial profile section for the Buyer Passport.
 *
 * Wraps InputsColumn with the design's section header treatment:
 *   - "FINANCIAL PROFILE" eyebrow
 *   - "Connect bank" + "Upload" action pills on the right
 *   - The InputsColumn rows beneath (icon + title + value + tier badge + edit icon)
 *   - EditAndVerifyDrawer slide-over for editing individual rows
 *
 * All functional wiring (Plaid connect, document upload, manual entries,
 * bank-income flag, tier gating) is preserved through InputsColumn unchanged.
 * The drawer wires to the SAME edit/manualEntries/verify machinery — no new
 * edit path is introduced.
 */

import { useState } from "react";
import { Button } from "../../ui/button";
import { InputsColumn } from "./inputs-column";
import type { InputsColumnManualEntries } from "./inputs-column";
import type { DashboardSnapshot } from "../../../lib/dashboard";
import type { ReadinessResult } from "../../../lib/readiness-types";
import type { ProfileRowId } from "../../../lib/readiness/factor-routing";
import type { UseProfileEditOptions } from "../../../lib/profile/use-profile-edit";
import { EditAndVerifyDrawer } from "./EditAndVerifyDrawer";
import { DrawerValueEditor } from "./DrawerValueEditor";
import { IncomeShapeEditor } from "./rows/income-shape-editor";
import type { ManualEntryKind, ManualFinancialEntry, ManualEntryRequest } from "../../../lib/onboarding-api";
import { bankComingSoonForRow } from "./inputs-column.helpers";
import { useVerificationFieldCapabilities } from "../../../hooks/useVerificationFieldCapabilities";
import { useFeatureFlagEnabled } from "@posthog/react";
import { PLAID_BANK_INCOME_FLAG } from "./inputs-column";

// ─── Human-readable labels for the drawer header ─────────────────────────────
const ROW_LABEL: Partial<Record<ProfileRowId, string>> = {
  income: "Income",
  cash_assets: "Cash & assets",
  debts: "Existing debts",
  personal_identity: "Personal & ID",
  employment: "Employment",
  credit: "Credit",
  // SILL-537 M5 (vf-shape-editable)
  income_shape: "How you earn",
};

// ─── Summary eyebrows for the drawer inset ───────────────────────────────────
const ROW_SUMMARY_EYEBROW: Partial<Record<ProfileRowId, string>> = {
  income: "Total income",
  cash_assets: "Total assets",
  debts: "Total debts",
};

// ─── Manual entry kind for the inline add form per row ───────────────────────
const ROW_ENTRY_KIND: Partial<Record<ProfileRowId, ManualEntryKind>> = {
  income: "income",
  cash_assets: "asset",
  debts: "liability",
};

export interface FinancialProfileSectionProps {
  snapshot: DashboardSnapshot;
  readiness: ReadinessResult | null | undefined;
  profileEditOpts?: UseProfileEditOptions;
  onConnectBank?: () => void;
  onUploadDocs?: () => void;
  onVerifyConnectBank?: (rowId?: ProfileRowId) => void;
  bankConnectLoadingRowId?: ProfileRowId | null;
  manualEntries?: InputsColumnManualEntries;
  getToken?: () => Promise<string | null>;
}

export function FinancialProfileSection({
  snapshot,
  readiness,
  profileEditOpts,
  onConnectBank,
  onUploadDocs,
  onVerifyConnectBank,
  bankConnectLoadingRowId,
  manualEntries,
  getToken,
}: FinancialProfileSectionProps) {
  // ── Drawer state ───────────────────────────────────────────────────────────
  const [openRowId, setOpenRowId] = useState<ProfileRowId | null>(null);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);

  const bankIncomeEnabled =
    useFeatureFlagEnabled(PLAID_BANK_INCOME_FLAG) ?? false;

  // Partition manual entries for the open row.
  const drawerEntries: ManualFinancialEntry[] = openRowId
    ? (manualEntries?.entries ?? []).filter((e) => {
        if (openRowId === "income") return e.entry_kind === "income";
        if (openRowId === "cash_assets") return e.entry_kind === "asset";
        if (openRowId === "debts") return e.entry_kind === "liability";
        return false;
      })
    : [];

  // Build the aggregate value string for the drawer summary from the snapshot.
  // snapshot.householdIncome = annual income in dollars
  // snapshot.savings = total assets in dollars
  // snapshot.monthlyDebts = monthly debts in dollars
  function drawerSummaryValue(rowId: ProfileRowId | null): string | undefined {
    if (!rowId) return undefined;
    if (rowId === "income") {
      const v = snapshot.householdIncome;
      if (v > 0) return `$${v.toLocaleString()}/yr`;
    }
    if (rowId === "cash_assets") {
      const v = snapshot.savings;
      if (v > 0) return `$${v.toLocaleString()}`;
    }
    if (rowId === "debts") {
      const v = snapshot.monthlyDebts;
      if (v > 0) return `$${v.toLocaleString()}/mo`;
    }
    return undefined;
  }

  const handleDeleteEntry = async (id: string) => {
    if (!manualEntries) return;
    setDeletingEntryId(id);
    try {
      await manualEntries.remove(id);
    } finally {
      setDeletingEntryId(null);
    }
  };

  // Which affordances a row can honour is the SERVER's answer (task 1678,
  // GET /api/verification/field-capabilities) — this component renders it
  // and never decides it. Replaces the old hardcoded BANK_VERIFIABLE_ROWS
  // set, and closes the gap on its document twin, which had no gate at all:
  // onUploadDocs was forwarded unconditionally, so "Upload document"
  // appeared on every row including "How you earn" (a six-value enum), Credit,
  // and Personal & ID.
  //
  // bankComingSoonForRow stays a SEPARATE, client-side condition. It is a
  // PostHog cohort gate on a capability the server says exists — a rollout
  // state, not an absence — so it renders the "coming soon" pill rather than
  // hiding the affordance.
  // Passed through as-is, undefined included: the hook skips the fetch
  // without a session rather than spending a guaranteed 401 and caching that
  // failure for the tab. No session means no CTAs on this section anyway.
  const capabilities = useVerificationFieldCapabilities(getToken);

  const drawerConnectBank =
    openRowId &&
    capabilities.supportsBank(openRowId) &&
    !bankComingSoonForRow(openRowId, bankIncomeEnabled)
      ? onConnectBank
      : undefined;

  const drawerUploadDocs =
    openRowId && capabilities.supportsDocument(openRowId)
      ? onUploadDocs
      : undefined;

  // Credit and employment are single-field, API-backed rows — host their
  // scalar editor inside the drawer so they stay editable (PATCH /api/profile).
  // income_shape (SILL-537 M5) is the same shape but ALSO re-reads the
  // verification path on save, so it hosts its own IncomeShapeEditor rather
  // than folding into the generic DrawerValueEditor's static ROW_CONFIG.
  const drawerValueEditor =
    profileEditOpts && (openRowId === "credit" || openRowId === "employment") ? (
      <DrawerValueEditor
        rowId={openRowId}
        snapshot={snapshot}
        opts={profileEditOpts}
      />
    ) : profileEditOpts && openRowId === "income_shape" ? (
      <IncomeShapeEditor snapshot={snapshot} opts={profileEditOpts} />
    ) : undefined;

  // The Sources (manual entries) section only applies to the three financial
  // list rows; hide it for rows without a manual-entry kind so they don't show
  // a dead "No sources yet" state.
  const drawerShowSources = openRowId
    ? Boolean(ROW_ENTRY_KIND[openRowId])
    : true;

  return (
    <section
      aria-labelledby="financial-profile-heading"
      data-testid="profile-inputs-section"
      // Deep-link anchor preserved from original profile-view.tsx
      id="verify"
    >
      {/* Section header row. flex-wrap: at phone widths the two action pills
          don't fit beside the eyebrow — they wrap below it instead of
          overlapping the label. */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <p
          className="text-[0.625rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground"
          id="financial-profile-heading"
        >
          Financial Profile
        </p>
        {/* Connect bank + Upload pills — shown when token is available */}
        {getToken && (
          <div
            data-testid="profile-inputs-verify-ctas"
            className="flex items-center gap-2"
          >
            {onConnectBank && (
              <Button
                variant="default"
                onClick={onConnectBank}
                data-testid="fp-connect-bank"
              >
                Connect bank
              </Button>
            )}
            {onUploadDocs && (
              <Button
                variant="outline"
                onClick={onUploadDocs}
                data-testid="fp-upload"
              >
                Upload
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Flat inline list — rows sit directly on page background, no card wrapper */}
      <div className="divide-y divide-border">
      <InputsColumn
        snapshot={snapshot}
        readiness={readiness}
        profileEditOpts={profileEditOpts}
        onConnectBank={onConnectBank ? (rowId: ProfileRowId) => {
          // Income routes to the income-mode Plaid flow via the rowId-aware
          // verify handler (#375 / SILL-339) — the PostHog flag gate + paywall
          // live there, so when the flag is off it stays parked. Other rows
          // open the section connect (initial mode).
          if (rowId === "income") {
            onVerifyConnectBank?.(rowId);
            return;
          }
          onConnectBank();
        } : undefined}
        onUploadDocs={onUploadDocs}
        capabilities={capabilities}
        onVerifyConnectBank={getToken ? onVerifyConnectBank : undefined}
        bankConnectLoadingRowId={bankConnectLoadingRowId}
        manualEntries={getToken ? manualEntries : undefined}
        onEdit={getToken ? (rowId) => setOpenRowId(rowId) : undefined}
      />
      </div>

      {/* Edit & Verify Drawer — wires to the same manualEntries machinery */}
      <EditAndVerifyDrawer
        open={openRowId !== null}
        onClose={() => setOpenRowId(null)}
        fieldLabel={ROW_LABEL[openRowId ?? "income"] ?? ""}
        rowId={openRowId ?? "income"}
        valueEditor={drawerValueEditor}
        showSources={drawerShowSources}
        onConnectBank={drawerConnectBank}
        onUploadDocs={drawerUploadDocs}
        // No !supportsDocument guard: the server sends a reason only when
        // the capability is false (pinned by TestFieldCapabilities_
        // UnsupportedCarriesReason), so this is already undefined otherwise.
        documentUnsupportedReason={
          openRowId ? capabilities.documentReason(openRowId) : undefined
        }
        bankComingSoon={
          openRowId ? bankComingSoonForRow(openRowId, bankIncomeEnabled) : false
        }
        summaryEyebrow={
          openRowId ? ROW_SUMMARY_EYEBROW[openRowId] : undefined
        }
        summaryValue={drawerSummaryValue(openRowId)}
        entries={drawerEntries}
        entriesLoading={manualEntries?.isLoading ?? false}
        addLabel="Add source"
        entryKind={openRowId ? ROW_ENTRY_KIND[openRowId] : undefined}
        onCreateSource={
          manualEntries && openRowId && ROW_ENTRY_KIND[openRowId]
            ? (req: ManualEntryRequest) => manualEntries.create(req)
            : undefined
        }
        onDeleteSource={manualEntries ? handleDeleteEntry : undefined}
        deletingId={deletingEntryId}
      />
    </section>
  );
}
